import { Fragment, ReactNode } from "react";
import {
  StructuredListBody,
  StructuredListCell,
  StructuredListRow,
  StructuredListWrapper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@carbon/react";
import { CheckmarkFilled, Email } from "@carbon/icons-react";
import i18next from "../../../i18n";
import { FileReader } from "../../../components";
import {
  BIDDING_TYPES,
  biddingInstants,
  formatBiddingDateTime,
  formatDate,
  labelType,
} from "../../../utils";
import { fileText } from "../../../utils/utils";
import { ORDER_TYPES, FileResponse } from "../../../types";
import ServicesToQuoteCard from "../../../components/ServicesToQuoteCard/ServicesToQuoteCard";
import { isQuotedByLine, servicesToQuoteOf } from "../serviceDetail";

/** Carbon replacement for the shared Tailwind `InfoItem` — one label/value summary row. */
const SummaryRow = ({
  name,
  description,
}: {
  name: string;
  description: ReactNode;
}) => (
  <div className="order-summary__info-row">
    <dt className="order-summary__info-label">{name}</dt>
    <dd className="order-summary__info-value">{description}</dd>
  </div>
);

/** Carbon replacement for the shared Tailwind `OrderFileList` — the summary's attachments. */
const SummaryFiles = ({ order }: { order: any }) => {
  const files: FileResponse[] = [
    ...((order?.files as FileResponse[]) ?? []),
    ...((order?.itemFiles as FileResponse[]) ?? []),
  ];
  if (!files.length) return null;
  return (
    <div className="order-summary__files">
      {files.map((file) => (
        <div key={file.fileURL} className="order-summary__file">
          <span className="order-summary__info-label">
            {fileText(file.type)}
          </span>
          <FileReader fileKey={file.fileURL} text={fileText(file.type)} />
        </div>
      ))}
    </div>
  );
};

type InvitedSupplier = {
  key: string;
  companyName?: string;
  name?: string;
  email?: string;
  validated?: boolean;
  isEmailInvite?: boolean;
};

const getInvitedSuppliers = (suppliers: any[] = []): InvitedSupplier[] => {
  const seen = new Set<string>();
  const result: InvitedSupplier[] = [];

  suppliers.forEach((sup, idx) => {
    if (!sup) return;
    if (sup.emailsList) {
      const emails = String(sup.emailsList)
        .split(/[:,]/)
        .map((e) => e.trim())
        .filter(Boolean);
      emails.forEach((email) => {
        const key = `email:${email}`;
        if (seen.has(key)) return;
        seen.add(key);
        result.push({ key, email, isEmailInvite: true });
      });
      return;
    }
    if (sup.id) {
      const key = `id:${sup.id}`;
      if (seen.has(key)) return;
      seen.add(key);
      result.push({
        key,
        companyName: sup.companyName,
        name: sup.name,
        email: sup.email,
        validated: sup.validated,
      });
      return;
    }
    if (sup.email) {
      const key = `email:${sup.email}`;
      if (seen.has(key)) return;
      seen.add(key);
      result.push({ key, email: sup.email, isEmailInvite: true });
      return;
    }
    const fallbackKey = `fallback:${idx}`;
    if (seen.has(fallbackKey)) return;
    seen.add(fallbackKey);
    result.push({
      key: fallbackKey,
      companyName: sup.companyName,
      name: sup.name,
      email: sup.email,
      validated: sup.validated,
    });
  });

  return result;
};

const SupplierRow = ({ supplier }: { supplier: InvitedSupplier }) => {
  const label =
    supplier.companyName ||
    supplier.name ||
    supplier.email ||
    i18next.t("messages.notAvailable");

  return (
    <StructuredListRow>
      <StructuredListCell className="order-summary__invited-icon-cell">
        {supplier.isEmailInvite ? (
          <Email className="order-summary__icon--invite" />
        ) : supplier.validated ? (
          <CheckmarkFilled className="order-summary__icon--validated" />
        ) : (
          <span className="order-summary__dot" />
        )}
      </StructuredListCell>
      <StructuredListCell>
        <span className="order-summary__supplier-name">{label}</span>
        {!supplier.isEmailInvite && supplier.email ? (
          <span className="order-summary__supplier-email">
            {supplier.email}
          </span>
        ) : null}
      </StructuredListCell>
      <StructuredListCell className="order-summary__invited-tag-cell">
        {supplier.isEmailInvite ? i18next.t("offer.invited") : null}
      </StructuredListCell>
    </StructuredListRow>
  );
};

const thInverse = [
  { id: 1, name: i18next.t("tenders.tenderSummary.code") },
  { id: 2, name: i18next.t("tenders.tenderSummary.material") },
  { id: 3, name: i18next.t("tenders.tenderSummary.volume") },
  { id: 4, name: i18next.t("tenders.tenderSummary.initialPrice") },
  { id: 5, name: i18next.t("tenders.tenderSummary.percentageDecrement") },
  { id: 6, name: i18next.t("tenders.tenderSummary.tenderPeriod") },
];

const thNormal = [
  { id: 1, name: i18next.t("tenders.tenderSummary.code") },
  { id: 2, name: i18next.t("tenders.tenderSummary.material") },
  { id: 3, name: i18next.t("tenders.tenderSummary.volume") },
  { id: 4, name: i18next.t("tenders.tenderSummary.tenderPeriod") },
];

type MultipleBiddingSummaryProps = {
  orderSummary: any;
  generalBidData: any;
  type: ORDER_TYPES;
  isBid?: boolean;
  showCompany?: boolean;
};

const MultipleBiddingSummary = ({
  type,
  isBid,
  orderSummary,
  generalBidData,
  showCompany,
}: MultipleBiddingSummaryProps) => {
  let tableTh = thNormal;

  if (type === ORDER_TYPES.INVERSE_JOINT || type === ORDER_TYPES.INVERSE) {
    tableTh = thInverse;
  }

  const allSuppliers = !isBid
    ? getInvitedSuppliers(
        (orderSummary || []).flatMap((order: any) => order?.suppliers || [])
      )
    : [];

  return (
    <div className="order-summary">
      <div>
        <div>
          {!isBid && (
            <h1 className="order-summary__title">
              {i18next.t("tenders.summary")}
            </h1>
          )}
          <SummaryRow
            name={i18next.t("tenders.form.tenderType")}
            description={labelType(type)}
          />
          {(type === ORDER_TYPES.JOINT || type === ORDER_TYPES.INVERSE_JOINT) &&
          isBid ? (
            <>
              <SummaryRow
                name={i18next.t("tenders.form.internalCode")}
                description={
                  <span className="order-summary__code">
                    {orderSummary[0]?.internalCode}
                  </span>
                }
              />
              <SummaryRow
                name={i18next.t("tenders.tenderSummary.initialDate")}
                description={formatBiddingDateTime(
                  orderSummary[0]?.opensAt,
                  orderSummary[0]?.startDate
                )}
              />
              <SummaryRow
                name={i18next.t("tenders.tenderSummary.endDate")}
                description={formatBiddingDateTime(
                  orderSummary[0]?.closesAt,
                  orderSummary[0]?.finishDate
                )}
              />
              <SummaryRow
                name={i18next.t("tenders.tenderSummary.paymentMethod")}
                description={orderSummary[0]?.paymentMethod}
              />
              <SummaryRow
                name={i18next.t("tenders.tenderSummary.paymentTerm")}
                description={`${orderSummary[0]?.paymentTerm} ${
                  orderSummary[0]?.paymentTerm === 1
                    ? ` ${i18next.t("opportunities.table.day")}`
                    : ` ${i18next.t("opportunities.table.days")}`
                }`}
              />
              {orderSummary[0]?.expireDate && (
                <SummaryRow
                  name={i18next.t("tenders.form.expireDate")}
                  description={i18next.t("cta.yes")}
                />
              )}
              {orderSummary[0]?.comment && (
                <SummaryRow
                  name={i18next.t("tenders.tenderSummary.comments")}
                  description={orderSummary[0]?.comment}
                />
              )}
              {generalBidData?.files?.length > 0 && (
                <SummaryFiles order={generalBidData} />
              )}
            </>
          ) : (
            <>
              <SummaryRow
                name={i18next.t("tenders.tenderSummary.initialDate")}
                description={formatBiddingDateTime(
                  biddingInstants(generalBidData ?? {}).opensAt,
                  generalBidData?.startDateValue
                )}
              />
              <SummaryRow
                name={i18next.t("tenders.tenderSummary.endDate")}
                description={formatBiddingDateTime(
                  biddingInstants(generalBidData ?? {}).closesAt,
                  generalBidData?.endDateValue
                )}
              />
              <SummaryRow
                name={i18next.t("tenders.tenderSummary.paymentMethod")}
                description={generalBidData?.paymentMethod}
              />
              <SummaryRow
                name={i18next.t("tenders.tenderSummary.paymentTerm")}
                description={`${generalBidData?.paymentTerm} ${
                  generalBidData?.paymentTerm === 1
                    ? ` ${i18next.t("opportunities.table.day")}`
                    : ` ${i18next.t("opportunities.table.days")}`
                }`}
              />
              {generalBidData?.expireDate && (
                <SummaryRow
                  name={i18next.t("tenders.form.expireDate")}
                  description={i18next.t("cta.yes")}
                />
              )}
              {generalBidData?.comment && (
                <SummaryRow
                  name={i18next.t("tenders.tenderSummary.comments")}
                  description={generalBidData?.comment}
                />
              )}
              <SummaryFiles order={generalBidData} />
            </>
          )}
        </div>
      </div>
      {!isBid && (
        <div className="order-summary__invited">
          <h2 className="order-summary__subtitle">
            {i18next.t("tenders.data.invited")}
          </h2>
          {allSuppliers.length > 0 ? (
            <StructuredListWrapper
              isCondensed
              className="order-summary__invited-list"
            >
              <StructuredListBody>
                {allSuppliers.map((sup) => (
                  <SupplierRow key={sup.key} supplier={sup} />
                ))}
              </StructuredListBody>
            </StructuredListWrapper>
          ) : (
            <p className="order-summary__empty">
              {i18next.t("suppliers.noSuppliersFound")}
            </p>
          )}
        </div>
      )}
      <Table className="order-summary__cds-table">
        <TableHead>
          <TableRow>
            {tableTh.map((t) => (
              <TableHeader key={t.id}>{t.name}</TableHeader>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {orderSummary?.map((order: any) => (
            <TableRow key={order.id}>
              <TableCell>
                <span className="order-materials__name">
                  {isBid ? order?.material?.ean : order.ean}
                </span>
              </TableCell>
              <TableCell>
                <span className="order-materials__name">
                  {isBid
                    ? order?.material?.globalMaterialName
                    : order.globalMaterialName}
                </span>
                {showCompany && order?.company?.name && (
                  <div className="order-materials__place">
                    {order.company.name}
                  </div>
                )}
              </TableCell>
              <TableCell>
                {isQuotedByLine(order)
                  ? i18next.t("serviceDetail.quantityPerLine")
                  : `${order.quantity ?? ""} ${order.measurementUnit ?? ""}`}
              </TableCell>
              {(type === BIDDING_TYPES.INVERSE ||
                type === BIDDING_TYPES.INVERSE_JOINT) && (
                <Fragment>
                  <TableCell>
                    {order.currency} {order.initialPrice}
                  </TableCell>
                  <TableCell>{order.minimumDecrement} %</TableCell>
                </Fragment>
              )}
              <TableCell>
                {order.startSupplyDate === order.endSupplyDate ? (
                  formatDate(order.startSupplyDate)
                ) : (
                  <>
                    {formatDate(order.startSupplyDate)} -{" "}
                    {formatDate(order.endSupplyDate)}
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!isBid && (
        <ServicesToQuoteCard services={servicesToQuoteOf(orderSummary)} />
      )}
    </div>
  );
};

export default MultipleBiddingSummary;
